import React, { useEffect, useState } from 'react';

import { FaTimes } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { trackFirstMemberBillingModalPageView } from '../../../acquisition/actions';
import { Link } from '../../../techstyle-shared/next-routes';
import { ImageAsset, useAsset } from '../../../techstyle-shared/react-assets';
import { Button, mobile } from '../../../techstyle-shared/react-components';
import { Modal as BaseModal } from '../../../techstyle-shared/react-components/src/Modal/Modal';
import {
  FormattedMessage,
  useIntl,
} from '../../../techstyle-shared/react-intl';
import { useLocalStorage } from '../../../techstyle-shared/redux-core';

const dialogStyle = css`
  ${({ theme: { colors, sizes } }) => `
    border-radius: ${sizes.sm}px;
    display: flex;
    flex-direction: column;
    align-items: center;
  `};
  width: 520px;
  height: 742px;
  position: relative;
  overflow: hidden;

  ${mobile`
    height: 550px;
  `};
`;

const Modal = styled(BaseModal).attrs({
  dialogStyle,
})`
  margin: 0 16px;
`;

const CloseButton = styled(Button).attrs({ variant: 'unstyled' })`
  position: absolute;
  ${({ theme: { colors, sizes } }) => `
    color: ${colors.palette.white};
    height: ${sizes.xxl}px;
    width: ${sizes.xxl}px;
    top: ${sizes.sm}px;;
    right: ${sizes.sm}px;
  `}

  span {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const StyledButton = styled(Button)`
  position: absolute;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  padding: 16px;
  text-transform: none;
  background-color: ${({ theme }) => theme.colors.palette.white};
  span {
    color: ${({ theme }) => theme.colors.palette.black};
    font-size: 18px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.palette.white};
    span {
      color: ${({ theme }) => theme.colors.palette.black};
    }
  }
`;

const CopyContainer = styled.div`
  position: absolute;
  width: 100%;
  bottom: 20%;
  text-align: center;
  color: ${({ theme }) => theme.colors.palette.white};
  font-size: 16px;
  z-index: 1;
  margin: 0 16px;
`;

const StyledHeader = styled.h2`
  color: ${({ theme }) => theme.colors.palette.white};
  font-size: 60px;
  padding: 0 40px;
  margin-bottom: 24px;
  text-align: center;
  ${({ theme }) => theme.fmbe.header}

  ${mobile`
    font-size: 32px;
  `}
`;

const StyledSubheading = styled.h3`
  color: ${({ theme }) => theme.colors.palette.white};
  font-size: 24px;
  margin-bottom: 16px;
  padding: 0 24px;
  text-align: center;
  ${({ theme }) => theme.fmbe.subHeader}
`;

const StyledImage = styled(ImageAsset)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  ${mobile`
    img {
      height: 550px;
      object-fit: cover;
    }
  `}
`;

const FirstMemberBillingModal = () => {
  const [modalIsOpen, setModalIsOpen] = useState(true);
  const intl = useIntl();
  const dispatch = useDispatch();

  const [hasShownModal, setHasShownModal] = useLocalStorage(
    'hasShownFirstMemberBillingModal'
  );

  const assetContainer = useAsset('first_credit_popup');
  const asset = assetContainer?.data?.assets?.[0];

  const closeModal = async () => {
    setModalIsOpen(false);
    // Prevent the modal from showing again after it has been closed
    setHasShownModal(true);
  };

  const shouldShowModal = !hasShownModal && asset;

  useEffect(() => {
    if (!shouldShowModal) {
      return;
    }

    dispatch(
      trackFirstMemberBillingModalPageView({
        pageName: '/fmbe-modal/',
      })
    );
  }, [dispatch, shouldShowModal]);

  if (!shouldShowModal) {
    return null;
  }

  return (
    <Modal
      title="First Member Billing"
      isOpen={modalIsOpen}
      onExit={closeModal}
    >
      <StyledImage asset={asset} />
      <CopyContainer>
        <StyledHeader>
          <FormattedMessage
            id="fmbe.header"
            defaultMessage="Your VIP Era Just Began"
          />
        </StyledHeader>
        <StyledSubheading>
          <FormattedMessage
            id="fmbe.subheading"
            defaultMessage="Your first credit is ready - shop VIP styles made just for you!"
          />
        </StyledSubheading>
      </CopyContainer>
      <Link href="/vip-exclusive-outfits">
        <a>
          <StyledButton
            variant="primary"
            data-autotag="fmbe_cta"
            onClick={closeModal}
          >
            <FormattedMessage
              id="fmbe.cta"
              defaultMessage="Explore VIP Credit Exclusives"
            />
          </StyledButton>
        </a>
      </Link>
      <CloseButton
        aria-label={intl.formatMessage({
          id: 'global_cta.close',
          defaultMessage: 'Close',
        })}
        data-autotag="fmbe_close"
        onClick={closeModal}
      >
        <FaTimes aria-hidden size={24} />
      </CloseButton>
    </Modal>
  );
};

export default FirstMemberBillingModal;
